/**
 * AI 助手「配置视图/临时应用配置」的纯函数辅助模块(与页面组件解耦,可单测)。
 *
 * 职责:
 *   1. validateViewConfigParams —— 校验大模型提交的视图配置(view_data)格式:
 *      结构 → 字段存在性 → 枚举 → 表达式,报错精确到字段
 *   2. normalizeViewData    —— 归一化(补 id 等),保证持久化数据规范
 *   3. findViewByUidOrName  —— 从视图列表按 uid/名称查找
 *
 * 字段存在性的依据:页面 baseColumns 的 dataIndex 与搜索字段列表(search_config),
 * 由调用方(remotePagesV2 页面)提供 —— 模型不得臆造字段名。
 */
import { genID } from 'liyu-pc-base/utils/function';
import { validateValueExpression } from 'liyu-pc-base/components/tablePro/utils';

export const VIEW_TYPES = ['normal', 'pivot', 'pivot_charts'] as const;
export type ViewType = (typeof VIEW_TYPES)[number];

export const isViewType = (t: unknown): t is ViewType =>
  typeof t === 'string' && (VIEW_TYPES as readonly string[]).includes(t);

const AGGREGATION_TYPES = [
  'SUM',
  'COUNT',
  'AVG',
  'MIN',
  'MAX',
  'NONE',
  'PRODUCT',
  'COUNTNUM',
  'STD',
  'STDP',
  'VAR',
  'VARP',
];
const CHART_TYPES = ['bar', 'line', 'pie', 'area', 'donut'];
const HIERARCHY_TYPES = ['grid', 'tree', 'grid-tree'];
const LINK_TYPES = ['_', '-', '()'];
const SORT_ORDERS = ['asc', 'desc'];
const TABLE_SIZES = ['small', 'middle', 'default'];
const REFRESH_TYPES = ['手动', '5s', '10s', '30s'];
const COLUMN_SORT_ORDERS = ['ascend', 'descend'];

export interface ValidateContext {
  /** 页面基础列(baseColumns),取 dataIndex 作为合法字段集合 */
  columns: any[];
  /** 展开后的搜索字段列表(advanced 展平 base_search_condition_config_list / 普通 searchConfig.fields) */
  search_config: any[];
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/** 展平列分组(children),取全部叶子列 */
function flattenColumns(columns: any[]): any[] {
  const list: any[] = [];
  const walk = (cols: any[]) => {
    (cols || []).forEach(col => {
      if (col.children && col.children.length > 0) {
        walk(col.children);
      } else {
        list.push(col);
      }
    });
  };
  walk(columns);
  return list;
}

/** 收集合法字段名集合(排除 index/action 等伪字段) */
function collectFieldNames(columns: any[]): Set<string> {
  const set = new Set<string>();
  flattenColumns(columns).forEach(col => {
    const key = col.dataIndex || col.key;
    if (key && key !== 'index' && key !== 'action') {
      set.add(key);
    }
  });
  return set;
}

const isPlainObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function fail(error: string): ValidationResult {
  return { valid: false, error };
}

/**
 * 校验基础视图数据(normal/pivot/pivot_charts 共用的部分):
 * columns(列显隐配置)/ searchConditions / tableSize / refreshType / sortedList
 */
function validateBaseViewData(viewData: any, ctx: ValidateContext): ValidationResult {
  if (!isPlainObject(viewData)) return fail('view_data 必须是一个对象');

  const fieldNames = collectFieldNames(ctx.columns);

  // columns: Record<dataIndex, ViewColumnItem>
  if (viewData.columns !== undefined && viewData.columns !== null) {
    if (!isPlainObject(viewData.columns)) return fail('view_data.columns 必须是对象(字段名 -> 列配置)');
    for (const key of Object.keys(viewData.columns)) {
      if (!fieldNames.has(key)) {
        return fail(`view_data.columns 含不存在的字段「${key}」,合法字段: ${[...fieldNames].join(', ')}`);
      }
      const item = viewData.columns[key];
      if (isPlainObject(item) && item.checked !== undefined && typeof item.checked !== 'boolean') {
        return fail(`view_data.columns.${key}.checked 必须是布尔值`);
      }
    }
    const hasChecked = Object.values(viewData.columns).some((item: any) => item?.checked);
    if (!hasChecked) return fail('view_data.columns 至少要有一个选中列(checked: true)');
  }

  // searchConditions: [{ field_name, operator, value[], format, class_name }]
  if (viewData.searchConditions !== undefined && viewData.searchConditions !== null) {
    if (!Array.isArray(viewData.searchConditions)) return fail('view_data.searchConditions 必须是数组');
    const searchFieldNames = new Set((ctx.search_config || []).map((s: any) => s.field_name));
    for (const cond of viewData.searchConditions) {
      if (!isPlainObject(cond)) return fail('view_data.searchConditions 每项必须是对象');
      if (!cond.field_name) return fail('view_data.searchConditions 每项必须有 field_name');
      if (searchFieldNames.size > 0 && !searchFieldNames.has(cond.field_name)) {
        return fail(`搜索条件含不存在的字段「${cond.field_name}」,合法搜索字段: ${[...searchFieldNames].join(', ')}`);
      }
      if (!Array.isArray(cond.value)) return fail(`搜索条件 ${cond.field_name} 的 value 必须是数组`);
    }
  }

  // tableSize / refreshType 枚举
  if (viewData.tableSize !== undefined && viewData.tableSize !== null) {
    if (!TABLE_SIZES.includes(viewData.tableSize)) {
      return fail(`view_data.tableSize 非法: ${viewData.tableSize}(可选: ${TABLE_SIZES.join('/')})`);
    }
  }
  if (viewData.refreshType !== undefined && viewData.refreshType !== null) {
    if (!REFRESH_TYPES.includes(viewData.refreshType)) {
      return fail(`view_data.refreshType 非法: ${viewData.refreshType}(可选: ${REFRESH_TYPES.join('/')})`);
    }
  }

  // sortedList: [{ key, order }]
  if (viewData.sortedList !== undefined && viewData.sortedList !== null) {
    if (!Array.isArray(viewData.sortedList)) return fail('view_data.sortedList 必须是数组');
    for (const s of viewData.sortedList) {
      if (!isPlainObject(s) || !s.key) return fail('view_data.sortedList 每项必须有 key');
      if (!fieldNames.has(s.key)) {
        return fail(`排序字段「${s.key}」不存在,合法字段: ${[...fieldNames].join(', ')}`);
      }
      if (s.order !== undefined && !COLUMN_SORT_ORDERS.includes(s.order)) {
        return fail(`排序方向 ${s.order} 非法(可选: ascend/descend)`);
      }
    }
  }

  return { valid: true };
}

/** 校验单个维度组(行/列/系列/类别) */
function validateDimGroup(group: any, fieldNames: Set<string>, where: string): ValidationResult {
  if (!isPlainObject(group)) return fail(`${where} 每项必须是对象`);
  const fields = Array.isArray(group.fields)
    ? group.fields
    : group.field // 兼容旧单字段结构 { field, label, processFunc }
      ? [group]
      : null;
  if (!fields) return fail(`${where} 缺少 fields 数组`);
  for (const f of fields) {
    if (!f.field) return fail(`${where} 字段缺少 field`);
    if (!fieldNames.has(f.field)) {
      return fail(`${where} 含不存在的字段「${f.field}」,合法字段: ${[...fieldNames].join(', ')}`);
    }
  }
  if (group.linkType !== undefined && !LINK_TYPES.includes(group.linkType)) {
    return fail(`${where} linkType 非法: ${group.linkType}(可选: _ / - / ())`);
  }
  if (group.sortOrder !== undefined && !SORT_ORDERS.includes(group.sortOrder)) {
    return fail(`${where} sortOrder 非法: ${group.sortOrder}(可选: asc/desc)`);
  }
  if (group.sortBy !== undefined && group.sortBy !== null && !fieldNames.has(group.sortBy)) {
    return fail(`${where} sortBy 字段「${group.sortBy}」不存在`);
  }
  if (
    group.subTotalAggregationType !== undefined &&
    !AGGREGATION_TYPES.includes(group.subTotalAggregationType)
  ) {
    return fail(`${where} subTotalAggregationType 非法: ${group.subTotalAggregationType}`);
  }
  return { valid: true };
}

/** 校验单个值字段(透视表值 / 透视图值,含表达式与过滤条件) */
function validateValueField(vf: any, fieldNames: Set<string>, where: string): ValidationResult {
  if (!isPlainObject(vf)) return fail(`${where} 每项必须是对象`);
  if (vf.field !== undefined && vf.field !== null && vf.field !== '' && !fieldNames.has(vf.field)) {
    return fail(`${where} 含不存在的字段「${vf.field}」,合法字段: ${[...fieldNames].join(', ')}`);
  }
  if (vf.aggregationType !== undefined && !AGGREGATION_TYPES.includes(vf.aggregationType)) {
    return fail(`${where} aggregationType 非法: ${vf.aggregationType}(可选: ${AGGREGATION_TYPES.join('/')})`);
  }
  for (const key of ['expression', 'filter']) {
    const tokens = vf[key];
    if (Array.isArray(tokens) && tokens.length > 0) {
      const r = validateValueExpression(tokens);
      if (!r.valid) return fail(`${where} ${key} 表达式非法: ${r.message}`);
    }
  }
  return { valid: true };
}

/** 校验透视配置(PivotConfig) */
function validatePivotConfig(pivot: any, ctx: ValidateContext): ValidationResult {
  if (!isPlainObject(pivot)) return fail('view_data.pivotConfig 必须是对象');
  const fieldNames = collectFieldNames(ctx.columns);

  for (const zone of ['rows', 'columns']) {
    if (!Array.isArray(pivot[zone])) return fail(`view_data.pivotConfig.${zone} 必须是数组`);
    for (let i = 0; i < pivot[zone].length; i++) {
      const r = validateDimGroup(pivot[zone][i], fieldNames, `view_data.pivotConfig.${zone}[${i}]`);
      if (!r.valid) return r;
    }
  }
  if (pivot.rows.length === 0 && pivot.columns.length === 0 && (pivot.values || []).length === 0) {
    return fail('view_data.pivotConfig 至少要有一个行/列维度或值字段');
  }

  if (!Array.isArray(pivot.values)) return fail('view_data.pivotConfig.values 必须是数组');
  for (let i = 0; i < pivot.values.length; i++) {
    const r = validateValueField(pivot.values[i], fieldNames, `view_data.pivotConfig.values[${i}]`);
    if (!r.valid) return r;
  }

  // 子表达式 / 聚合计算值
  if (pivot.subExpressions !== undefined) {
    if (!Array.isArray(pivot.subExpressions)) return fail('view_data.pivotConfig.subExpressions 必须是数组');
    for (let i = 0; i < pivot.subExpressions.length; i++) {
      const se = pivot.subExpressions[i];
      const r = validateValueField({ expression: se?.expression }, fieldNames, `subExpressions[${i}]`);
      if (!r.valid) return r;
    }
  }
  if (pivot.aggComputedValues !== undefined) {
    if (!Array.isArray(pivot.aggComputedValues)) return fail('view_data.pivotConfig.aggComputedValues 必须是数组');
    const aggRefs = pivot.aggComputedValues.map((a: any) => ({
      id: a?.id,
      label: a?.name || a?.id,
      expression: a?.expression,
    }));
    for (let i = 0; i < pivot.aggComputedValues.length; i++) {
      const ac = pivot.aggComputedValues[i];
      if (!ac || !ac.name) return fail(`view_data.pivotConfig.aggComputedValues[${i}] 缺少 name`);
      if (!Array.isArray(ac.expression) || ac.expression.length === 0) {
        return fail(`view_data.pivotConfig.aggComputedValues[${i}] 缺少 expression`);
      }
      const r = validateValueExpression(ac.expression, { aggComputedRefs: aggRefs });
      if (!r.valid) return fail(`聚合计算值「${ac.name}」表达式非法: ${r.message}`);
      if (ac.totalAggregationMode !== undefined && !['recompute', 'sum_leaf'].includes(ac.totalAggregationMode)) {
        return fail(`聚合计算值「${ac.name}」totalAggregationMode 非法`);
      }
    }
  }

  // 枚举/结构选项
  if (pivot.indicatorsAsCol !== undefined && typeof pivot.indicatorsAsCol !== 'boolean') {
    return fail('view_data.pivotConfig.indicatorsAsCol 必须是布尔值');
  }
  if (pivot.rowHierarchyType !== undefined && !HIERARCHY_TYPES.includes(pivot.rowHierarchyType)) {
    return fail(`view_data.pivotConfig.rowHierarchyType 非法: ${pivot.rowHierarchyType}(可选: ${HIERARCHY_TYPES.join('/')})`);
  }
  for (const k of ['frozenRowCount', 'frozenColCount']) {
    if (pivot[k] !== undefined && (typeof pivot[k] !== 'number' || pivot[k] < 0)) {
      return fail(`view_data.pivotConfig.${k} 必须是非负数字`);
    }
  }
  return { valid: true };
}

/** 校验透视图配置(PivotChartsConfig) */
function validatePivotChartsConfig(charts: any, ctx: ValidateContext): ValidationResult {
  if (!isPlainObject(charts)) return fail('view_data.pivotChartsConfig 必须是对象');
  const fieldNames = collectFieldNames(ctx.columns);

  for (const zone of ['series', 'category']) {
    if (!Array.isArray(charts[zone])) return fail(`view_data.pivotChartsConfig.${zone} 必须是数组`);
    for (let i = 0; i < charts[zone].length; i++) {
      const r = validateDimGroup(charts[zone][i], fieldNames, `view_data.pivotChartsConfig.${zone}[${i}]`);
      if (!r.valid) return r;
    }
  }
  if (!Array.isArray(charts.values)) return fail('view_data.pivotChartsConfig.values 必须是数组');
  for (let i = 0; i < charts.values.length; i++) {
    const r = validateValueField(charts.values[i], fieldNames, `view_data.pivotChartsConfig.values[${i}]`);
    if (!r.valid) return r;
  }
  if (charts.chartType !== undefined && !CHART_TYPES.includes(charts.chartType)) {
    return fail(`view_data.pivotChartsConfig.chartType 非法: ${charts.chartType}(可选: ${CHART_TYPES.join('/')})`);
  }
  if (charts.showPercent !== undefined && typeof charts.showPercent !== 'boolean') {
    return fail('view_data.pivotChartsConfig.showPercent 必须是布尔值');
  }
  return { valid: true };
}

/**
 * 校验视图配置数据(view_type + view_data),不含名称等附加字段。
 * 供 page_view_config / page_view_update 共用。
 */
export function validateViewData(
  view_type: unknown,
  viewData: unknown,
  ctx: ValidateContext,
): ValidationResult {
  if (!isViewType(view_type)) {
    return fail(`view_type 必填: ${VIEW_TYPES.join(' | ')}`);
  }
  if (!isPlainObject(viewData)) return fail('view_data 必须是对象(视图配置)');

  // JSON 可序列化(禁止函数/循环引用)
  try {
    JSON.stringify(viewData);
  } catch {
    return fail('view_data 包含不可序列化的内容(函数/循环引用)');
  }

  const base = validateBaseViewData(viewData, ctx);
  if (!base.valid) return base;

  if (view_type === 'pivot') {
    if (viewData.pivotConfig === undefined) return fail('pivot 视图必须包含 view_data.pivotConfig');
    return validatePivotConfig(viewData.pivotConfig, ctx);
  }
  if (view_type === 'pivot_charts') {
    if (viewData.pivotChartsConfig === undefined) return fail('pivot_charts 视图必须包含 view_data.pivotChartsConfig');
    return validatePivotChartsConfig(viewData.pivotChartsConfig, ctx);
  }
  return { valid: true };
}

/**
 * 校验大模型提交的视图配置参数。
 * @param params  { view_type, view_data, apply_mode?, name?, is_system? }
 * @param ctx     页面字段上下文(columns / search_config)
 */
export function validateViewConfigParams(
  params: any,
  ctx: ValidateContext,
): ValidationResult {
  if (!isPlainObject(params)) return fail('参数必须是对象');
  const apply_mode =
    params.apply_mode === undefined || params.apply_mode === null ? 'create' : params.apply_mode;
  if (!['create', 'temporary'].includes(apply_mode)) {
    return fail('apply_mode 非法(可选: create / temporary)');
  }
  if (apply_mode === 'create' && (typeof params.name !== 'string' || !params.name.trim())) {
    return fail('创建视图时,视图名称 name 不能为空');
  }
  if (params.is_system !== undefined && typeof params.is_system !== 'boolean') {
    return fail('is_system 必须是布尔值');
  }
  return validateViewData(params.view_type, params.view_data, ctx);
}

// 兼容旧命名,避免调用方一次性改名时出现中断。
export const validateAddViewParams = validateViewConfigParams;

/**
 * 归一化视图数据:为透视表/透视图配置补 id(维度组、值字段、子表达式、聚合计算值),
 * 保证持久化数据规范、组件加载时可稳定还原。其余结构原样保留。
 */
export function normalizeViewData(viewData: any, view_type: ViewType): any {
  if (!isPlainObject(viewData)) return viewData;
  const clone = JSON.parse(JSON.stringify(viewData));
  const ensureId = (item: any) => {
    if (item && !item.id) item.id = genID();
    return item;
  };
  const normalizeDims = (list: any[]) => {
    (list || []).forEach((group: any) => {
      if (Array.isArray(group.fields)) {
        ensureId(group);
      } else if (group.field) {
        // 旧单字段结构 → 归一为维度组
        group.fields = [{ field: group.field, label: group.label, processFunc: group.processFunc }];
        group.id = group.id || genID();
        delete group.field;
      } else {
        group.id = group.id || genID();
      }
    });
  };
  const config =
    view_type === 'pivot'
      ? clone.pivotConfig
      : view_type === 'pivot_charts'
        ? clone.pivotChartsConfig
        : null;
  if (config && isPlainObject(config)) {
    if (view_type === 'pivot') {
      normalizeDims(config.rows);
      normalizeDims(config.columns);
      (config.values || []).forEach(ensureId);
      (config.subExpressions || []).forEach(ensureId);
      (config.aggComputedValues || []).forEach(ensureId);
    } else {
      normalizeDims(config.series);
      normalizeDims(config.category);
      (config.values || []).forEach(ensureId);
      (config.subExpressions || []).forEach(ensureId);
      (config.aggComputedValues || []).forEach(ensureId);
    }
  }
  return clone;
}

/**
 * 从视图列表按 uid(优先)/名称查找视图。
 * @param list fetchViews 后的视图列表(每项含 uid/name/data)
 */
export function findViewByUidOrName(
  list: any[],
  uid?: string | null,
  name?: string | null,
): any | null {
  if (!Array.isArray(list)) return null;
  if (uid) {
    const byUid = list.find(v => String(v.uid) === String(uid));
    if (byUid) return byUid;
  }
  if (name) {
    const byName = list.find(v => String(v.name) === String(name).trim());
    if (byName) return byName;
  }
  return null;
}
